/**
 * SupportCard — карточка ресурсов поддержки, появляется после ответа коуча
 * когда обнаружен эмоциональный дистресс.
 *
 * Props:
 *   level     — 'support' | 'urgent'
 *   onDismiss — () => void (кнопка закрыть, опционально)
 *
 * Канон conversation-ui.mdc: inline-стили + var(--color-*) токены,
 * никаких BEM-классов без CSS, никаких emoji в контенте.
 */

import { useState } from 'react'

import { apiCoachSupportRequest } from '../../api/aiCoach'
import { Globe, LifeBuoy, Phone, Send, X } from 'lucide-react'

const RESOURCES = {
    support: {
        heading: 'Поддержка рядом',
        body: 'Если нужна живая помощь — HR-партнёр, психолог или кризисная линия.',
        accentVar: 'var(--color-accent)',
        items: [
            { label: 'Таиланд', value: '1323', tel: '1323' },
            { label: 'Россия', value: '8-800-2000-122', tel: '88002000122' },
            { label: 'Весь мир', value: 'befrienders.org', href: 'https://www.befrienders.org' },
        ],
    },
    urgent: {
        heading: 'Ты не один — помощь рядом',
        body: 'Позвони прямо сейчас. Линии работают круглосуточно и бесплатно.',
        accentVar: 'var(--color-warning)',
        items: [
            { label: 'Таиланд', value: '1323', tel: '1323' },
            { label: 'Россия', value: '8-800-2000-122', tel: '88002000122' },
            { label: 'SMS/чат (США)', value: 'HOME → 741741', href: 'https://www.crisistextline.org' },
            { label: 'Весь мир', value: 'befrienders.org', href: 'https://www.befrienders.org' },
        ],
    },
}

export default function SupportCard({ level = 'support', onDismiss }) {
    const cfg = RESOURCES[level] || RESOURCES.support
    const [hrState, setHrState] = useState('idle') // idle | sending | sent | error

    const handleHrRequest = async () => {
        if (hrState !== 'idle') return
        setHrState('sending')
        try {
            await apiCoachSupportRequest()
            setHrState('sent')
        } catch {
            setHrState('error')
        }
    }

    return (
        <div
            role="complementary"
            aria-label="Ресурсы поддержки"
            style={{
                margin: '0 16px 8px',
                padding: '12px 14px',
                borderLeft: `2px solid ${cfg.accentVar}`,
                background: 'var(--color-bg-soft)',
                borderRadius: '0 8px 8px 0',
                position: 'relative',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <LifeBuoy
                    size={16}
                    style={{ color: cfg.accentVar, flexShrink: 0, marginTop: 2 }}
                    aria-hidden="true"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            marginBottom: 4,
                            color: 'var(--color-text)',
                        }}
                    >
                        {cfg.heading}
                    </div>
                    <div
                        style={{
                            fontSize: '0.78rem',
                            color: 'var(--color-text-muted)',
                            marginBottom: 8,
                            lineHeight: 1.4,
                        }}
                    >
                        {cfg.body}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                        {cfg.items.map((item, i) => {
                            const href = item.href ?? (item.tel ? `tel:${item.tel}` : null)
                            const Icon = item.tel ? Phone : Globe
                            return (
                                <a
                                    key={i}
                                    href={href}
                                    target={item.href && !item.tel ? '_blank' : undefined}
                                    rel={item.href ? 'noopener noreferrer' : undefined}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        color: 'var(--color-accent)',
                                        textDecoration: 'none',
                                        padding: '2px 0',
                                    }}
                                >
                                    <Icon size={11} aria-hidden="true" />
                                    <span>
                                        {item.label}: {item.value}
                                    </span>
                                </a>
                            )
                        })}
                    </div>

                    {/* HR warm handoff button */}
                    <div style={{ marginTop: 10, borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
                        {hrState === 'sent' ? (
                            <p
                                style={{
                                    fontSize: '0.76rem',
                                    color: 'var(--color-success)',
                                    margin: 0,
                                }}
                            >
                                HR-партнёр получил уведомление. Тема разговора не передаётся.
                            </p>
                        ) : hrState === 'error' ? (
                            <p
                                style={{
                                    fontSize: '0.76rem',
                                    color: 'var(--color-danger)',
                                    margin: 0,
                                }}
                            >
                                Не удалось отправить. Обратись напрямую к HR.
                            </p>
                        ) : (
                            <button
                                type="button"
                                onClick={handleHrRequest}
                                disabled={hrState === 'sending'}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    fontSize: '0.76rem',
                                    fontWeight: 600,
                                    color: 'var(--color-accent)',
                                    background: 'none',
                                    border: '1px solid var(--color-accent)',
                                    borderRadius: 6,
                                    padding: '3px 10px',
                                    cursor: hrState === 'sending' ? 'wait' : 'pointer',
                                    opacity: hrState === 'sending' ? 0.6 : 1,
                                }}
                            >
                                <Send size={11} aria-hidden="true" />
                                {hrState === 'sending' ? 'Отправка…' : 'Уведомить HR-партнёра'}
                            </button>
                        )}
                        <p
                            style={{
                                fontSize: '0.7rem',
                                color: 'var(--color-text-muted)',
                                margin: '4px 0 0',
                                lineHeight: 1.3,
                            }}
                        >
                            Содержание разговора не передаётся — только факт обращения.
                        </p>
                    </div>
                </div>
                {onDismiss ? (
                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label="Скрыть карточку поддержки"
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            color: 'var(--color-text-muted)',
                            flexShrink: 0,
                        }}
                    >
                        <X size={14} />
                    </button>
                ) : null}
            </div>
        </div>
    )
}
